import { useQueryClient } from "@tanstack/react-query";
import {
  apiErrorMessage,
  useCandidatePage,
  useJob,
  useMoveToSelected,
  useSendToOutreach,
  useSourcingRun,
  useStartSourcing,
} from "@vidario/api-client";
import {
  ACTIVE_FILTER_NOUN,
  activeFilterCount,
  CANDIDATE_NOUN,
  countLabel,
  effectiveFilters,
  filtersForTab,
  withTabFilters,
  formatCount,
  formatDateDE,
  furthestStep,
  isSourcingActive,
  jobPlaceLine,
  longlistEmptyState,
  pageRangeLabel,
  RESET_LABEL,
  type Candidate,
  type CandidateFilters,
  type CandidateSort,
  type CandidateStatus,
  type CandidateTab,
  type ResetScope,
} from "@vidario/domain";
import {
  Breadcrumb,
  Button,
  Card,
  cn,
  EmptyState,
  Input,
  JobStatusBadge,
  Pagination,
  ProcessStepper,
  Select,
  Spinner,
  useToast,
} from "@vidario/ui";
import {
  Building,
  Calendar,
  Check,
  Edit,
  Filter,
  Lock,
  MapPin,
  RefreshCw,
  Search,
  Send,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { ANONYM_OUTLINE } from "../../anonym/outline";
import { useAnonymView } from "../../anonym/useAnonymView";
import { useRole } from "../../guards/useRole";
import { RecommendationPanel } from "../recommendation/RecommendationPanel";
import { RecruitersDialog } from "./RecruitersDialog";
import { CandidateCard } from "./CandidateCard";
import { CandidateFilterChips } from "./CandidateFilterChips";
import { CandidateFiltersPanel } from "./CandidateFiltersPanel";
import { CloseJobDialog } from "./CloseJobDialog";
import { RestartSourcingDialog } from "./RestartSourcingDialog";
import { SourcingProgress } from "./SourcingProgress";
import { useCandidateFilters, writeToParams } from "./useCandidateFilters";

type Tab = "longlist" | "selected" | "recommendation" | "pool";

const SORTS: readonly CandidateSort[] = ["score", "location", "addedAt"];
/** How long the search field waits before asking the server (#14). */
const SEARCH_DEBOUNCE_MS = 300;
const NO_CANDIDATES = "Keine Kandidaten";

type SortKey = NonNullable<CandidateFilters["sortBy"]>;

/** Longlist screen (MVP §4 Screen 3). Two-tab layout:
 * A (Longlist) shows the candidates in status longlist; rejected ones
 * aren't listed in any tab (#26). B (Ausgewählte Talente) shows candidates the recruiter has
 * moved forward. "Send to Outreach" batch-advances selected candidates
 * to status=outreach. */
export function LonglistRoute(): ReactElement {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = useRole();
  const isAdmin = role === "admin";
  const toast = useToast();
  const anonym = useAnonymView();

  const { data: job, isLoading: jobLoading, error: jobError } = useJob(jobId);
  const { data: sourcingRun, isLoading: runLoading } = useSourcingRun(jobId);
  const qc = useQueryClient();
  const runStatus = sourcingRun?.status;

  // Each stage of a running search can promote candidates onto the Longlist
  // (#170), so pull them in when the stage changes. When the sourcing pipeline
  // finishes, pull in the candidates it produced — and the job itself. The finder geocodes the job as part of the run, so
  // `distanceAvailable` can flip false → true; without refetching the job the
  // radius control stays disabled until a full reload, which is exactly the
  // step the disabled hint asks the recruiter to take ("… und starte die
  // Kandidatensuche neu").
  useEffect(() => {
    if (!runStatus) return;
    void qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
    if (!isSourcingActive(runStatus)) void qc.invalidateQueries({ queryKey: ["jobs", jobId] });
  }, [runStatus, jobId, qc]);

  const rawTab = ((params.get("tab") as Tab | null) ?? "longlist") as Tab;
  const sort = (params.get("sort") ?? "score") as SortKey;
  // In the Anonym-Ansicht a search term stays out of the URL: the address bar is
  // on the shared screen and the browser would suggest the name later (#145).
  const [anonymSearch, setAnonymSearch] = useState("");
  const [anonymWas, setAnonymWas] = useState(anonym);
  if (anonym !== anonymWas) {
    setAnonymWas(anonym);
    setAnonymSearch("");
  }
  const search = anonym ? anonymSearch : (params.get("search") ?? "");

  // Any change but the page itself starts again at page 1 (#14).
  const updateParams = (
    patch: Record<string, string | null>,
    options?: { replace?: boolean },
  ): void => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!("page" in patch)) next.delete("page");
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      options?.replace ? { replace: true } : undefined,
    );
  };
  const page = Math.max(1, Number(params.get("page")) || 1);
  // A link or reload with ?search= while the mode is already on: drop it from the address bar.
  const urlSearchWhileAnonym = anonym && params.has("search");
  useEffect(() => {
    if (urlSearchWhileAnonym) updateParams({ search: null }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is recreated each render
  }, [urlSearchWhileAnonym]);

  // The search field answers at once; the URL (and the server) follow after a pause.
  const [searchInput, setSearchInput] = useState(search);
  const searchRef = useRef<HTMLInputElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const tabRowRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  // Back/forward or a reset changes the URL's search: show it in the field too.
  const [syncedSearch, setSyncedSearch] = useState(search);
  if (search !== syncedSearch) {
    setSyncedSearch(search);
    setSearchInput(search);
  }
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(() => {
      if (!anonym) {
        updateParams({ search: searchInput }, { replace: true });
        return;
      }
      setAnonymSearch(searchInput);
      updateParams({}, { replace: true }); // back to page 1, like any other change
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is recreated each render
  }, [searchInput, search, anonym]);

  // Ticked candidates survive page changes; their status decides which bulk action
  // takes them, since the other pages' cards aren't loaded.
  const [selected, setSelected] = useState<ReadonlyMap<string, CandidateStatus>>(new Map());
  const [restartOpen, setRestartOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [recruitersOpen, setRecruitersOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { filters, setFilters } = useCandidateFilters();

  const sendToOutreach = useSendToOutreach();
  const moveToSelected = useMoveToSelected();
  const restartSourcing = useStartSourcing();

  const handleRestartSourcing = (): void => {
    restartSourcing.mutate(jobId, {
      onSuccess: () => {
        setRestartOpen(false);
        toast.push({
          kind: "success",
          title: "Kandidatensuche neu gestartet",
          message: "Vidario läuft erneut die Sourcing-Pipeline.",
        });
        // Refresh the inline sourcing-progress panel.
        void qc.invalidateQueries({ queryKey: ["jobs", jobId, "sourcing"] });
        // The backend purges the non-selected candidates synchronously on
        // restart, so refetch the candidate list — the Longlist shows empty
        // immediately while the new run refills it.
        void qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
      },
      onError: (err) => {
        toast.push({
          kind: "error",
          title: "Neustart fehlgeschlagen",
          message: apiErrorMessage(err),
        });
      },
    });
  };

  // A closed job's candidates are frozen (#19): no status actions.
  const jobClosed = job?.status === "closed";

  // The server filters, sorts, counts and pages (#14, ADR 0006). The pool tab
  // is admin-only; a recruiter landing on ?tab=pool gets the Longlist.
  const listTab: CandidateTab =
    rawTab === "selected" ? "selected" : rawTab === "pool" && isAdmin ? "pool" : "longlist";
  // A radius the job cannot support isn't an active filter — derive the badge,
  // the chips and the list from one value so all three agree (issue #4).
  // Aussortiert keeps only the filters its rows can meet (#200).
  // Memoised: `effectiveFilters` allocates a fresh object when it drops the
  // radius, which would invalidate the list/count memos below on every render
  // — they are deliberately kept stable for ~600-candidate lists.
  const shownFilters = useMemo(
    () => filtersForTab(effectiveFilters(filters, job ?? undefined), listTab),
    [filters, job, listTab],
  );
  const filterCount = activeFilterCount(shownFilters);
  // A change on a tab touches only the filters it shows (#200).
  const setTabFilters = (next: CandidateFilters): void =>
    setFilters(withTabFilters(filters, next, listTab));
  const query = useMemo(
    () => ({
      tab: listTab,
      search,
      sort: SORTS.includes(sort as CandidateSort) ? (sort as CandidateSort) : "score",
      page,
      filters: shownFilters,
    }),
    [listTab, search, sort, page, shownFilters],
  );
  const {
    data: pageData,
    isLoading: listLoading,
    isPlaceholderData: refreshing,
    error: pageError,
    refetch: refetchPage,
  } = useCandidatePage(jobId, query);
  // A failed request is an error, never an empty list (#14 review).
  const listFailed = Boolean(pageError) && !pageData;
  const list = pageData?.results ?? [];
  const tabCounts = pageData?.tabCounts;
  const hasRecommendation = (tabCounts?.recommendation ?? 0) > 0;

  // Tab is only visible once a candidate is in the recommendation. For a
  // recruiter that means after the release: before it, the API shows them
  // as `in_review` (ADR 0004). Fall back to the Longlist tab if someone
  // lands on ?tab=recommendation too early.
  const tab: Tab =
    (rawTab === "recommendation" && pageData && !hasRecommendation) ||
    (rawTab === "pool" && !isAdmin)
      ? "longlist"
      : rawTab;

  // Keep the active tab in view when the row scrolls sideways, without moving the page.
  // The row only exists once the job has loaded, and gains tabs as counts arrive.
  useEffect(() => {
    const row = tabRowRef.current;
    const active = activeTabRef.current;
    if (!row || !active || row.scrollWidth <= row.clientWidth) return;
    row.scrollLeft = active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2;
  }, [tab, job, hasRecommendation, isAdmin]);

  const emptyState =
    !listLoading && !runLoading && !listFailed && list.length === 0 && tab !== "recommendation"
      ? longlistEmptyState({
          tab,
          search,
          filterCount,
          run: sourcingRun,
          movedOnCount: tabCounts?.selected ?? 0,
          isAdmin,
          jobClosed,
          jobEditable: job?.status !== "closed" && job?.status !== "placed",
        })
      : null;

  // One URL write: two setParams calls in the same tick don't compose.
  const resetQuery = (scope: ResetScope): void => {
    const clearsSearch = scope !== "filters";
    const clearsFilters = scope !== "search";
    if (clearsSearch) {
      setSearchInput("");
      setAnonymSearch("");
    }
    setParams(
      (prev) => {
        const next = clearsFilters
          ? writeToParams(prev, withTabFilters(filters, {}, listTab))
          : new URLSearchParams(prev);
        if (clearsSearch) next.delete("search");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
    // The control whose state changed; the search field only when the search was cleared,
    // so a phone's keyboard doesn't cover the list for a filter reset (#163).
    (clearsSearch ? searchRef : filterButtonRef).current?.focus();
  };

  // A filter that shrank the result can leave the URL past the last page;
  // follow the page the server actually returned.
  useEffect(() => {
    if (pageData && !refreshing && pageData.page !== page) {
      updateParams({ page: pageData.page === 1 ? null : String(pageData.page) }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is recreated each render
  }, [pageData, refreshing, page]);

  // Selection is cleared whenever what's listed changes (tab, search, filter),
  // so a bulk action never reaches candidates the user can no longer see (#26).
  const listedKey = `${rawTab}|${search}|${JSON.stringify(shownFilters)}`;
  const [selectionKey, setSelectionKey] = useState(listedKey);
  if (listedKey !== selectionKey) {
    setSelectionKey(listedKey);
    setSelected(new Map());
  }

  const changeTab = (next: Tab): void => {
    if (next === tab) return;
    updateParams({ tab: next === "longlist" ? null : next });
  };

  const gridRef = useRef<HTMLDivElement>(null);
  const changePage = (next: number): void => {
    updateParams({ page: next === 1 ? null : String(next) });
    gridRef.current?.scrollIntoView({ block: "start" });
  };

  const toggleSelect = (c: Candidate): void => {
    const next = new Map(selected);
    if (next.has(c.id)) next.delete(c.id);
    else next.set(c.id, c.status);
    setSelected(next);
  };
  const selectedWith = (status: CandidateStatus): string[] =>
    [...selected].filter(([, s]) => s === status).map(([id]) => id);

  const handleSendToOutreach = (ids: readonly string[]): void => {
    if (ids.length === 0) return;
    sendToOutreach.mutate(
      { jobId, candidateIds: ids },
      {
        onSuccess: () => {
          toast.push({
            kind: "success",
            title: "Outreach gestartet",
            message: `${countLabel(ids.length, CANDIDATE_NOUN)} ${ids.length === 1 ? "wird" : "werden"} vom Vidario-Team kontaktiert.`,
          });
          setSelected(new Map());
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const handleOutreachSelection = (): void => handleSendToOutreach(selectedWith("selected"));

  const handleMoveToSelected = (ids: readonly string[]): void => {
    if (ids.length === 0) return;
    moveToSelected.mutate(
      { jobId, candidateIds: ids },
      {
        onSuccess: () => {
          toast.push({
            kind: "success",
            title: "In Ausgewählte Talente verschoben",
            message: `${countLabel(ids.length, CANDIDATE_NOUN)} markiert.`,
          });
          setSelected(new Map());
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const handleMoveSelection = (): void => handleMoveToSelected(selectedWith("longlist"));

  if (jobLoading) {
    return (
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
        <p className="text-sm text-text-secondary" role="status">
          Lade Stelle…
        </p>
      </main>
    );
  }
  if (jobError || !job) {
    return (
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
        <p className="text-sm text-danger-600" role="alert">
          Stelle konnte nicht geladen werden.
        </p>
      </main>
    );
  }

  const currentStep = furthestStep(pageData?.statuses ?? []);

  return (
    <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8 pb-20">
      <Breadcrumb
        className="mb-5"
        items={[
          { label: "Dashboard", onClick: () => void navigate("/dashboard") },
          { label: job.title },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0 flex-1 basis-[360px]">
          <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">{job.title}</h1>
          <div
            className="mt-2 flex flex-wrap items-center gap-4 text-sm text-text-secondary"
            data-testid="job-header"
          >
            <span className="inline-flex items-center gap-1.5">
              <Building size={14} /> {job.company}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} /> {jobPlaceLine(job.location, job.workModel, { short: true })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={14} /> eröffnet {formatDateDE(job.createdAt)}
            </span>
            <JobStatusBadge status={job.status} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {isAdmin && (
            <>
              <Button
                variant="secondary"
                leftIcon={<RefreshCw size={16} />}
                onClick={() => setRestartOpen(true)}
                loading={restartSourcing.isPending}
                disabled={job.status === "closed"}
              >
                Kandidatensuche neu starten
              </Button>
            </>
          )}
          {/* Matches the edit page, which treats placed and closed jobs as read-only. */}
          {job.status !== "closed" && job.status !== "placed" && (
            <Button
              variant="secondary"
              leftIcon={<Edit size={16} />}
              onClick={() => void navigate(`/jobs/${job.id}/edit`)}
            >
              Bearbeiten
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="secondary"
              leftIcon={<Users size={16} />}
              onClick={() => setRecruitersOpen(true)}
            >
              Recruiter verwalten
            </Button>
          )}
          {job.status !== "closed" && (
            <Button
              variant="destructive-ghost"
              leftIcon={<Lock size={16} />}
              onClick={() => setCloseOpen(true)}
            >
              Stelle schließen
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="mb-5 rounded-md bg-[linear-gradient(to_right,var(--admin-marker-bg),transparent_50%)] py-2.5 pl-3 pr-4 text-xs font-medium text-[color:var(--admin-marker)]">
          <span className="uppercase tracking-wider">Vidario-intern</span> · Kandidaten-Verwaltung
          nur für Admin-Rolle sichtbar
        </div>
      )}

      {sourcingRun && <SourcingProgress run={sourcingRun} jobId={jobId} isAdmin={isAdmin} />}

      <Card className="mb-6 px-8 py-6">
        <ProcessStepper currentStep={currentStep < 0 ? 0 : currentStep} />
      </Card>

      {/* On a phone the tabs scroll inside their row instead of widening the page (#124). */}
      <div className="mb-6 border-b border-border-default">
        <div ref={tabRowRef} className="relative -mb-px flex gap-1 overflow-x-auto pr-6 lg:pr-0">
          {(
            [
              { id: "longlist" as const, label: "Longlist", count: tabCounts?.longlist },
              { id: "selected" as const, label: "Ausgewählte Talente", count: tabCounts?.selected },
              ...(hasRecommendation
                ? [
                    {
                      id: "recommendation" as const,
                      label: "Empfehlungen",
                      count: tabCounts?.recommendation,
                    },
                  ]
                : []),
              // Admin-only view of what the search found but sorted out.
              ...(isAdmin
                ? [{ id: "pool" as const, label: "Aussortiert", count: tabCounts?.pool }]
                : []),
            ] satisfies { id: Tab; label: string; count?: number | undefined }[]
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                ref={active ? activeTabRef : undefined}
                type="button"
                onClick={() => changeTab(t.id)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4.5 py-3 text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
                  active
                    ? "border-primary-500 font-semibold text-primary-700"
                    : "border-transparent font-medium text-neutral-500 hover:text-neutral-800",
                )}
              >
                {t.label}
                {t.count !== undefined && (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                      active
                        ? "bg-primary-100 text-primary-700"
                        : "bg-neutral-100 text-neutral-600",
                    )}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "recommendation" && <RecommendationPanel jobId={jobId} />}

      {/* Nothing to move while the Longlist has no candidates (or its count is loading),
          unless marks are waiting. A search with no hits keeps it, so the toolbar
          below doesn't jump while the user types (#163). */}
      {tab === "longlist" &&
        !jobClosed &&
        ((tabCounts?.longlist ?? 0) > 0 || selected.size > 0) && (
          <div
            className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-100 bg-primary-50 px-5 py-3.5"
            data-testid="move-to-selected-bar"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-neutral-0">
                <Check size={16} />
              </span>
              <div>
                <div className="text-sm font-semibold">In Ausgewählte Talente verschieben</div>
                <div className="text-xs text-text-secondary">
                  {selected.size > 0
                    ? `${countLabel(selected.size, CANDIDATE_NOUN)} markiert`
                    : "Markiere die Kandidaten, die in die engere Auswahl wandern sollen"}
                </div>
              </div>
            </div>
            <Button
              disabled={selected.size === 0}
              leftIcon={<Check size={16} />}
              onClick={handleMoveSelection}
              loading={moveToSelected.isPending}
            >
              Talente auswählen
            </Button>
          </div>
        )}

      {tab === "selected" && !jobClosed && (
        <div
          className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-100 bg-primary-50 px-5 py-3.5"
          data-testid="send-to-outreach-bar"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-neutral-0">
              <Send size={16} />
            </span>
            <div>
              <div className="text-sm font-semibold">Nächster Schritt: Outreach starten</div>
              <div className="text-xs text-text-secondary">
                {selected.size > 0
                  ? `${countLabel(selected.size, CANDIDATE_NOUN)} markiert`
                  : "Markiere die Kandidaten, die kontaktiert werden sollen"}
              </div>
            </div>
          </div>
          <Button
            disabled={selected.size === 0}
            leftIcon={<Send size={16} />}
            onClick={handleOutreachSelection}
            loading={sendToOutreach.isPending}
          >
            Outreach starten
          </Button>
        </div>
      )}

      {tab !== "recommendation" && (
        <>
          <div className="mb-5 flex flex-wrap items-end gap-3">
            <div className="max-w-sm flex-1 basis-72">
              <Input
                icon={<Search size={16} />}
                placeholder="Kandidat suchen…"
                ref={searchRef}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Kandidatensuche"
              />
            </div>
            <div className="min-w-40">
              <Select
                aria-label="Sortierung"
                value={sort}
                onValueChange={(v) => updateParams({ sort: v })}
                options={[
                  {
                    value: "score",
                    label: listTab === "pool" ? "Sortieren: Vorauswahl" : "Sortieren: Match-Score",
                  },
                  { value: "location", label: "Sortieren: Entfernung" },
                  { value: "addedAt", label: "Sortieren: Datum hinzugefügt" },
                ]}
              />
            </div>
            <Button
              variant="secondary"
              leftIcon={<Filter size={16} />}
              onClick={() => setFiltersOpen((open) => !open)}
              active={filtersOpen}
              aria-expanded={filtersOpen}
              aria-controls="candidate-filters-panel"
              ref={filterButtonRef}
            >
              Filter
              {filterCount > 0 && (
                <span
                  className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-[11px] font-semibold text-neutral-0"
                  aria-label={countLabel(filterCount, ACTIVE_FILTER_NOUN)}
                >
                  {filterCount}
                </span>
              )}
            </Button>
            <div className="flex-1" />
            <div className="inline-flex items-center gap-1.5 text-[13px] text-text-tertiary">
              <Users size={14} aria-hidden="true" />{" "}
              {pageData && countLabel(pageData.count, CANDIDATE_NOUN, { zero: NO_CANDIDATES })}
              {/* Here, not in the pager, so a one-page list shows it too (#152). */}
              {refreshing && <Spinner label="Lädt" />}
              {(pageData?.hiddenWithoutLocation ?? 0) > 0 && (
                <span title="Für diese Kandidaten ist kein Wohnort bekannt.">
                  · {formatCount(pageData?.hiddenWithoutLocation ?? 0)} ohne Wohnort ausgeblendet
                </span>
              )}
            </div>
          </div>

          <CandidateFilterChips
            filters={shownFilters}
            onChange={setTabFilters}
            vorauswahl={listTab === "pool"}
            // Filters that only work on another tab get one line, so none is
            // set without a sign (#201). Recruiters have no Aussortiert.
            otherTab={isAdmin ? { filters, tab: listTab } : null}
          />

          {/* List + inline filter panel as flex siblings. The panel is
              always visible while open so the recruiter can adjust
              sliders and watch the list react live; the list keeps its
              auto-fill grid and just reflows to fewer columns when the
              panel takes 380px of the row. */}
          {/* Below lg the panel stacks above the list it filters (#124); it comes
              first in the DOM, right after the Filter button, and moves right on lg. */}
          <div
            className={cn("flex flex-col gap-6 lg:flex-row", filtersOpen ? "lg:items-start" : "")}
          >
            {filtersOpen && (
              <CandidateFiltersPanel
                filters={filtersForTab(filters, listTab)}
                distanceAvailable={job?.distanceAvailable ?? true}
                vorauswahl={listTab === "pool"}
                onChange={setTabFilters}
                onReset={() => setTabFilters({})}
                onClose={() => setFiltersOpen(false)}
              />
            )}
            <div className="min-w-0 flex-1">
              {listLoading && (
                <p className="text-sm text-text-secondary" role="status">
                  Lade Kandidaten…
                </p>
              )}

              {listFailed && (
                <div role="alert" className="flex flex-col items-start gap-3">
                  <p className="m-0 text-sm text-danger-600">
                    Kandidaten konnten nicht geladen werden.
                  </p>
                  {pageError && (
                    <p className="m-0 text-sm text-text-secondary">{apiErrorMessage(pageError)}</p>
                  )}
                  <Button variant="secondary" onClick={() => void refetchPage()}>
                    Erneut versuchen
                  </Button>
                </div>
              )}

              {emptyState && (
                <EmptyState
                  icon={<Users size={28} />}
                  title={emptyState.title}
                  description={emptyState.description}
                  action={
                    emptyState.reset && (
                      <Button
                        variant="secondary"
                        leftIcon={<RefreshCw size={16} />}
                        onClick={() => emptyState.reset && resetQuery(emptyState.reset)}
                      >
                        {RESET_LABEL[emptyState.reset]}
                      </Button>
                    )
                  }
                />
              )}

              {/* Only the cards show candidate data, so only they get the outline;
                  it sits outside the grid so it doesn't fade while refreshing (#152). */}
              {list.length > 0 && (
                <div className={cn(anonym && ANONYM_OUTLINE)}>
                  <div
                    ref={gridRef}
                    className={cn(
                      "grid scroll-mt-6 gap-4 grid-cols-[repeat(auto-fill,minmax(min(21rem,100%),1fr))]",
                      refreshing && "opacity-60 motion-safe:transition-opacity",
                    )}
                    aria-busy={refreshing}
                    data-testid="longlist-candidates"
                  >
                    {list.map((c) => {
                      const movable = !jobClosed && tab === "longlist" && c.status === "longlist";
                      const outreachable =
                        !jobClosed && tab === "selected" && c.status === "selected";
                      const lockedCheckbox =
                        jobClosed ||
                        (tab === "selected" && c.status !== "selected") ||
                        (tab === "longlist" && c.status !== "longlist");
                      return (
                        <CandidateCard
                          key={c.id}
                          job={job}
                          candidate={c}
                          selected={selected.has(c.id)}
                          onToggle={() => toggleSelect(c)}
                          onOpen={() => void navigate(`/jobs/${jobId}/candidates/${c.id}`)}
                          disabled={lockedCheckbox}
                          {...(movable && {
                            onMoveToSelected: () => handleMoveToSelected([c.id]),
                            moveInProgress: moveToSelected.isPending,
                          })}
                          {...(outreachable && {
                            onSendToOutreach: () => handleSendToOutreach([c.id]),
                            outreachInProgress: sendToOutreach.isPending,
                          })}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* On one page the pager would only repeat the toolbar's count (#152). */}
              {pageData && pageData.pageCount > 1 && (
                <Pagination
                  className={anonym ? "mt-8" : "mt-6"}
                  page={pageData.page}
                  pageCount={pageData.pageCount}
                  onPageChange={changePage}
                  summary={pageRangeLabel(pageData.page, pageData.pageSize, pageData.count)}
                />
              )}
              <p className="sr-only" role="status">
                {pageData
                  ? [
                      `${countLabel(pageData.count, CANDIDATE_NOUN, { zero: NO_CANDIDATES })} gefunden`,
                      ...(pageData.pageCount > 1
                        ? [`Seite ${pageData.page} von ${pageData.pageCount}`]
                        : []),
                    ].join(" · ")
                  : ""}
              </p>
            </div>
          </div>
        </>
      )}

      <CloseJobDialog job={job} open={closeOpen} onOpenChange={setCloseOpen} />

      {isAdmin && (
        <>
          <RestartSourcingDialog
            jobId={jobId}
            open={restartOpen}
            onOpenChange={setRestartOpen}
            onConfirm={handleRestartSourcing}
            loading={restartSourcing.isPending}
            onAdjustParams={() => {
              setRestartOpen(false);
              void navigate(`/jobs/${job.id}/edit`);
            }}
          />
          <RecruitersDialog open={recruitersOpen} onOpenChange={setRecruitersOpen} job={job} />
        </>
      )}
    </main>
  );
}
